import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Edit,
  Save,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  FileText,
  Download,
  Clock,
  CheckCircle,
  AlertTriangle,
  Loader,
  DollarSign,
  ExternalLink,
  Plus,
  Trash2
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'
import { apiRequest } from '@/config/api'
import AssociatePositionModal from '@/components/candidates/AssociatePositionModal'
import RecruiterResumeUpload from '@/components/candidates/RecruiterResumeUpload'

interface ActivePosition {
  id: string
  positionId: string
  eventNumber: number
  positionTitle: string
  positionLocation: string
  salaryRange: string
  status: string
  currentStage: string
  stages: {
    shortlisted: { completed: boolean; date?: string }
    recruiterCall: { completed: boolean; date?: string }
    interviewRound1: { completed: boolean; date?: string }
    interviewRound2: { completed: boolean; date?: string }
  }
  result: string
  resultDate?: string
  appliedDate: string
}

interface CandidateResume {
  uuid: string
  original_filename: string
  file_size: number
  file_type: string
  is_primary: boolean
  is_coverletter: boolean
  is_loaded_by_recruiter: boolean
  resume_description?: string
  upload_status: string
  created_at: string
  updated_at: string
}

type CandidateSkill =
  | string
  | {
      id?: string
      uuid?: string
      skillName?: string
      skill_name?: string
      name?: string
      skillCategory?: string
      category?: string
      proficiencyLevel?: string
      level?: string
      yearsOfExperience?: number
      years?: number
      isPrimarySkill?: boolean
      is_primary?: boolean
    }

const SKILL_CATEGORIES = [
  'Technical',
  'Programming Languages',
  'Frameworks',
  'Databases',
  'Cloud Platforms',
  'Tools',
  'Soft Skills',
  'Languages',
  'Other'
]

const PROFICIENCY_OPTIONS = ['beginner', 'intermediate', 'advanced', 'expert']

const generateSkillId = () => `skill-${Math.random().toString(36).slice(2, 10)}`

const getSkillLabel = (skill: CandidateSkill): string => {
  if (typeof skill === 'string') return skill
  return skill?.skillName || skill?.skill_name || skill?.name || ''
}

const ensureSkillObject = (skill: CandidateSkill | undefined, fallbackId?: string): CandidateSkill => {
  const id = fallbackId || generateSkillId()
  if (skill && typeof skill !== 'string') {
    return {
      ...skill,
      id: skill.id || id,
      uuid: skill.uuid || skill.id || id,
      skillName: skill.skillName || skill.skill_name || skill.name || '',
      skillCategory: skill.skillCategory || skill.category || 'Technical',
      proficiencyLevel: skill.proficiencyLevel || skill.level || 'intermediate',
      yearsOfExperience: skill.yearsOfExperience ?? skill.years ?? 0,
      isPrimarySkill: skill.isPrimarySkill ?? skill.is_primary ?? false
    }
  }

  return {
    id,
    uuid: id,
    skillName: typeof skill === 'string' ? skill : '',
    skillCategory: 'Technical',
    proficiencyLevel: 'intermediate',
    yearsOfExperience: 0,
    isPrimarySkill: false
  }
}

const normalizeSkills = (skills?: CandidateSkill[]): CandidateSkill[] => {
  if (!Array.isArray(skills)) return []
  return skills.map((skill, index) => ensureSkillObject(skill, `skill-${index}`))
}

const serializeSkills = (skills?: CandidateSkill[]): string[] => {
  if (!Array.isArray(skills)) return []
  return skills
    .map(skill => getSkillLabel(skill).trim())
    .filter(skill => !!skill)
}

interface CandidateDetail {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  location: string
  position: string
  positionId: string
  status: string
  isActive: boolean
  appliedDate: string
  interviewDate?: string
  experience: number
  education: string
  skills: CandidateSkill[]
  expectedSalary?: number
  resumeUrl?: string
  coverLetter?: string
  linkedinUrl?: string
  portfolioUrl?: string
  overallScore: number
  technicalScore: number
  behavioralScore: number
  aiScore: number
  recruiterRating: number
  aiInsights: string[]
  activePositions?: ActivePosition[]
  createdByMe?: boolean
  createdById?: string | number
  createdByEmail?: string
  notRegistered?: boolean
}

export default function CandidateDetailPage({ params }: { params: { id: string } }) {
  const navigate = useNavigate()
  const [candidate, setCandidate] = useState<CandidateDetail | null>(null)
  const [candidateResumes, setCandidateResumes] = useState<CandidateResume[]>([])
  const [loadingResumes, setLoadingResumes] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [uploadingResume, setUploadingResume] = useState(false)
  const [resumeUploadError, setResumeUploadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showAssociateModal, setShowAssociateModal] = useState(false)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [newSkill, setNewSkill] = useState('')
  const [addingSkill, setAddingSkill] = useState(false)
  const [skillError, setSkillError] = useState<string | null>(null)

  useEffect(() => {
    loadCandidateDetail()
    loadCandidateResumes()
  }, [params.id])

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiClient.getCurrentUser()
        if (response.success && response.data) {
          setCurrentUser(response.data)
        }
      } catch (err) {
        console.error('Failed to load current user:', err)
      }
    }

    loadCurrentUser()
  }, [])

  const canManageSkills = useMemo(() => {
    if (!candidate || !candidate.notRegistered) return false
    if (candidate.createdByMe) return true
    if (!currentUser) return false

    const creatorIdMatches = candidate.createdById && currentUser.id && String(candidate.createdById) === String(currentUser.id)
    const creatorEmailMatches = candidate.createdByEmail && currentUser.email && candidate.createdByEmail.toLowerCase() === currentUser.email.toLowerCase()

    return !!(creatorIdMatches || creatorEmailMatches)
  }, [candidate, currentUser])

  const loadCandidateDetail = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.getCandidate(params.id)
      
      if (response.success && response.data) {
        const candidateData = response.data.candidate || response.data
        setCandidate({
          id: candidateData.id?.toString() || params.id,
          firstName: candidateData.first_name || candidateData.firstName || '',
          lastName: candidateData.last_name || candidateData.lastName || '',
          email: candidateData.email || '',
          phone: candidateData.phone || '',
          location: candidateData.location || '',
          position: candidateData.position_title || candidateData.position || '',
          positionId: candidateData.position_id?.toString() || '',
          status: candidateData.status || 'new',
          isActive: candidateData.status !== 'inactive',
          appliedDate: candidateData.applied_date || candidateData.appliedDate || new Date().toISOString(),
          interviewDate: candidateData.interview_date || candidateData.interviewDate,
          experience: candidateData.years_experience || candidateData.experience || 0,
          education: candidateData.education || '',
          skills: normalizeSkills(candidateData.skills),
          expectedSalary: candidateData.expected_salary || candidateData.expectedSalary,
          resumeUrl: candidateData.resume_url || candidateData.resumeUrl,
          coverLetter: candidateData.cover_letter || candidateData.coverLetter || '',
          linkedinUrl: candidateData.linkedin_url || candidateData.linkedinUrl || '',
          portfolioUrl: candidateData.portfolio_url || candidateData.portfolioUrl || '',
          overallScore: candidateData.overall_score || candidateData.overallScore || 0,
          technicalScore: candidateData.technical_score || candidateData.technicalScore || 0,
          behavioralScore: candidateData.behavioral_score || candidateData.behavioralScore || 0,
          aiScore: candidateData.ai_score || candidateData.aiScore || 0,
          recruiterRating: candidateData.recruiter_rating || candidateData.recruiterRating || 0,
          aiInsights: candidateData.ai_insights || candidateData.aiInsights || [],
          activePositions: candidateData.activePositions || [],
          createdByMe: candidateData.created_by_me || candidateData.createdByMe || candidateData.isCreatedByMe,
          createdById: candidateData.created_by || candidateData.createdBy || candidateData.created_by_id || candidateData.createdById || candidateData.created_by_user_id,
          createdByEmail: candidateData.created_by_email || candidateData.createdByEmail || candidateData.creator_email,
          notRegistered: candidateData.not_registered ?? candidateData.notRegistered ?? false
        })
      } else {
        setError('Candidate not found')
      }
    } catch (err) {
      setError('Failed to load candidate details')
      console.error('Failed to load candidate:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadCandidateResumes = async () => {
    try {
      setLoadingResumes(true)
      
      // Use the new endpoint to get resumes for the specific candidate
      const response = await apiRequest(`file-uploads/candidate/${params.id}/resumes`, {
        method: 'GET'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.resumes) {
          setCandidateResumes(data.resumes)
        }
      } else {
        console.error('Failed to load candidate resumes')
      }
    } catch (err) {
      console.error('Error loading candidate resumes:', err)
    } finally {
      setLoadingResumes(false)
    }
  }

  const handleInputChange = (field: keyof CandidateDetail, value: any) => {
    setCandidate(prev => prev ? { ...prev, [field]: value } : null)
  }

  const handleResumeUploadSuccess = async (s3Key: string, originalFilename: string) => {
    try {
      setUploadingResume(true)
      setResumeUploadError(null)
      
      // Update the candidate record with the new resume URL
      const bucket_name = 'hirehubble-user-uploads' // This should match your S3 bucket
      const resumeUrl = `https://${bucket_name}.s3.amazonaws.com/${s3Key}`
      
      // Update candidate with new resume URL
      const updateData = {
        resume_url: resumeUrl
      }
      
      const response = await apiClient.updateCandidate(params.id, updateData)
      
      if (response.success) {
        setCandidate(prev => prev ? { ...prev, resumeUrl: resumeUrl } : null)
        alert('Resume uploaded and updated successfully!')
        loadCandidateDetail() // Refresh data
        loadCandidateResumes() // Refresh resumes list
      } else {
        throw new Error(response.error || 'Failed to update candidate with resume URL')
      }
    } catch (err) {
      setResumeUploadError('Failed to update candidate with resume: ' + handleApiError(err))
    } finally {
      setUploadingResume(false)
    }
  }

  const handleResumeUploadError = (error: string) => {
    setResumeUploadError(error)
    setUploadingResume(false)
  }

  const handleAddSkill = async () => {
    if (!candidate || !canManageSkills) return
    const skillToAdd = newSkill.trim()
    if (!skillToAdd) {
      setSkillError('Enter a skill before adding')
      return
    }

    setAddingSkill(true)
    setSkillError(null)

    try {
      const updatedSkills = [...(candidate.skills || []), ensureSkillObject(skillToAdd, generateSkillId())]
      const response = await apiClient.updateCandidateSkills(params.id, {
        skills: serializeSkills(updatedSkills)
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to add skill')
      }

      setCandidate(prev => prev ? { ...prev, skills: updatedSkills } : prev)
      setNewSkill('')
    } catch (err) {
      setSkillError(handleApiError(err))
    } finally {
      setAddingSkill(false)
    }
  }

  const handleSkillFieldChange = (index: number, field: string, value: any) => {
    setSkillError(null)
    setCandidate(prev => {
      if (!prev) return prev
      const updated = [...(prev.skills || [])]
      updated[index] = {
        ...ensureSkillObject(updated[index], generateSkillId()),
        [field]: value
      }
      return { ...prev, skills: updated }
    })
  }

  const handleRemoveSkill = (index: number) => {
    setSkillError(null)
    setCandidate(prev => {
      if (!prev) return prev
      const updated = [...(prev.skills || [])]
      updated.splice(index, 1)
      return { ...prev, skills: updated }
    })
  }

  const handleAddSkillRow = () => {
    setSkillError(null)
    setCandidate(prev => {
      if (!prev) return prev
      const updated = [...(prev.skills || []), ensureSkillObject(undefined, generateSkillId())]
      return { ...prev, skills: updated }
    })
  }

  const handleSave = async () => {
    if (!candidate) return

    setSaving(true)
    try {
      const skillNames = serializeSkills(candidate.skills)
      if (canManageSkills && candidate.skills && candidate.skills.length > skillNames.length) {
        setSkillError('Please provide a name for every skill or remove unused rows before saving.')
        setSaving(false)
        return
      }

      const updateData = {
        first_name: candidate.firstName,
        last_name: candidate.lastName,
        email: candidate.email,
        phone: candidate.phone,
        location: candidate.location,
        years_experience: candidate.experience,
        education: candidate.education,
        expected_salary: candidate.expectedSalary,
        linkedin_url: candidate.linkedinUrl,
        portfolio_url: candidate.portfolioUrl
      }

      const response = await apiClient.updateCandidate(params.id, updateData)
      
      if (!response.success) {
        throw new Error(response.error || 'Update failed')
      }

      if (canManageSkills) {
        const skillsResponse = await apiClient.updateCandidateSkills(params.id, { skills: skillNames })
        if (!skillsResponse.success) {
          throw new Error(skillsResponse.error || 'Failed to update skills')
        }
        setSkillError(null)
      }

      setIsEditing(false)
      alert('Candidate updated successfully!')
      loadCandidateDetail()
    } catch (err) {
      alert('Failed to update candidate: ' + handleApiError(err))
    } finally {
      setSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800'
      case 'screening': return 'bg-purple-100 text-purple-800'
      case 'interviewed': return 'bg-orange-100 text-orange-800'
      case 'shortlisted': return 'bg-green-100 text-green-800'
      case 'offered': return 'bg-indigo-100 text-indigo-800'
      case 'hired': return 'bg-emerald-100 text-emerald-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'applied': return 'bg-blue-100 text-blue-800'
      case 'shortlisted': return 'bg-purple-100 text-purple-800'
      case 'recruiter_call': return 'bg-yellow-100 text-yellow-800'
      case 'interview_round_1': return 'bg-orange-100 text-orange-800'
      case 'interview_round_2': return 'bg-indigo-100 text-indigo-800'
      case 'selected': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      case 'on_hold': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading candidate details...</p>
        </div>
      </div>
    )
  }

  if (error || !candidate) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Candidate</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {candidate.firstName} {candidate.lastName}
            </h1>
            <p className="text-gray-600">{candidate.position}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.open(`/public/candidate-profile/${params.id}`, '_blank')}
            className="btn-secondary"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View Public Profile
          </button>
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                disabled={saving}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={candidate.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900">{candidate.firstName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={candidate.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900">{candidate.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={candidate.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${candidate.email}`} className="text-blue-600 hover:underline">
                      {candidate.email}
                    </a>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={candidate.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${candidate.phone}`} className="text-blue-600 hover:underline">
                      {candidate.phone}
                    </a>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={candidate.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">{candidate.location}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience (Years)</label>
                {isEditing ? (
                  <input
                    type="number"
                    min="0"
                    value={candidate.experience}
                    onChange={(e) => handleInputChange('experience', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">{candidate.experience} years</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={candidate.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <GraduationCap className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">{candidate.education || 'Not specified'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Salary</label>
                {isEditing ? (
                  <input
                    type="number"
                    min="0"
                    value={candidate.expectedSalary || ''}
                    onChange={(e) => handleInputChange('expectedSalary', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Annual salary expectation"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">
                      {candidate.expectedSalary ? `$${candidate.expectedSalary.toLocaleString()}` : 'Not specified'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* URLs */}
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                {isEditing ? (
                  <input
                    type="url"
                    value={candidate.linkedinUrl}
                    onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://linkedin.com/in/..."
                  />
                ) : candidate.linkedinUrl ? (
                  <a href={candidate.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {candidate.linkedinUrl}
                  </a>
                ) : (
                  <span className="text-gray-500">Not provided</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio URL</label>
                {isEditing ? (
                  <input
                    type="url"
                    value={candidate.portfolioUrl}
                    onChange={(e) => handleInputChange('portfolioUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://portfolio.com"
                  />
                ) : candidate.portfolioUrl ? (
                  <a href={candidate.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {candidate.portfolioUrl}
                  </a>
                ) : (
                  <span className="text-gray-500">Not provided</span>
                )}
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Skills</h3>
              {canManageSkills && !isEditing && (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add skill"
                    disabled={addingSkill}
                  />
                  <button
                    onClick={handleAddSkill}
                    disabled={addingSkill}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {addingSkill ? 'Adding...' : 'Add'}
                  </button>
                </div>
              )}
            </div>
            {skillError && (
              <p className="text-sm text-red-600 mb-3">{skillError}</p>
            )}
            {isEditing && canManageSkills ? (
              <div className="space-y-3">
                {(candidate.skills || []).map((skill, index) => {
                  const skillObj = ensureSkillObject(skill, `skill-${index}`)
                  return (
                    <div key={skillObj.uuid || skillObj.id || index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={skillObj.skillName || ''}
                          onChange={(e) => handleSkillFieldChange(index, 'skillName', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Skill name"
                        />
                        <button
                          type="button"
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          onClick={() => handleRemoveSkill(index)}
                          title="Remove skill"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                          <select
                            value={skillObj.skillCategory || 'Technical'}
                            onChange={(e) => handleSkillFieldChange(index, 'skillCategory', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {SKILL_CATEGORIES.map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Proficiency</label>
                          <select
                            value={skillObj.proficiencyLevel || 'intermediate'}
                            onChange={(e) => handleSkillFieldChange(index, 'proficiencyLevel', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {PROFICIENCY_OPTIONS.map(option => (
                              <option key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Years of Experience</label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={skillObj.yearsOfExperience ?? 0}
                            onChange={(e) => handleSkillFieldChange(index, 'yearsOfExperience', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!skillObj.isPrimarySkill}
                          onChange={(e) => handleSkillFieldChange(index, 'isPrimarySkill', e.target.checked)}
                          className="rounded"
                        />
                        Mark as primary skill
                      </label>
                    </div>
                  )
                })}
                <button
                  type="button"
                  onClick={handleAddSkillRow}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Skill
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(candidate.skills || []).map((skill, index) => {
                  const skillName = getSkillLabel(skill) || 'Skill'
                  const key = typeof skill === 'string'
                    ? `${skill}-${index}`
                    : skill?.uuid || skill?.id || `${skillName}-${index}`

                  return (
                    <span key={key} className="inline-flex px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                      {skillName}
                    </span>
                  )
                })}
                {(candidate.skills || []).length === 0 && (
                  <span className="text-gray-500">No skills listed</span>
                )}
              </div>
            )}
          </div>

          {/* Active Positions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Active Positions</h3>
              <button
                onClick={() => setShowAssociateModal(true)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Add Position
              </button>
            </div>
            {candidate.activePositions && candidate.activePositions.length > 0 ? (
              <div className="space-y-4">
                {candidate.activePositions.map((position) => (
                  <div key={`${position.positionId}-${position.eventNumber}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{position.positionTitle}</h4>
                        <p className="text-sm text-gray-600">
                          {position.positionLocation} • Event #{position.eventNumber}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStageColor(position.currentStage)}`}>
                          {position.currentStage.replace('_', ' ')}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Applied: {new Date(position.appliedDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Progress Stages */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Progress:</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(position.result)}`}>
                          {position.result}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center space-x-1">
                          {position.stages.shortlisted.completed ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={position.stages.shortlisted.completed ? 'text-green-700' : 'text-gray-500'}>
                            Shortlisted
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          {position.stages.recruiterCall.completed ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={position.stages.recruiterCall.completed ? 'text-green-700' : 'text-gray-500'}>
                            Recruiter Call
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          {position.stages.interviewRound1.completed ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={position.stages.interviewRound1.completed ? 'text-green-700' : 'text-gray-500'}>
                            Interview R1
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          {position.stages.interviewRound2.completed ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={position.stages.interviewRound2.completed ? 'text-green-700' : 'text-gray-500'}>
                            Interview R2
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {position.salaryRange && (
                      <div className="mt-2 text-xs text-gray-600">
                        Salary: {position.salaryRange}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="text-sm font-medium text-gray-900 mb-2">No Active Positions</h4>
                <p className="text-sm text-gray-600">This candidate is not currently active for any position.</p>
              </div>
            )}
          </div>

          {/* Cover Letter */}
          {candidate.coverLetter && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cover Letter</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {candidate.coverLetter}
              </p>
            </div>
          )}

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Resume */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumes</h3>
            
            {loadingResumes ? (
              <div className="flex items-center space-x-2 text-gray-500 mb-4">
                <Loader className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading resumes...</span>
              </div>
            ) : candidateResumes.length > 0 ? (
              <div className="space-y-3 mb-4">
                {candidateResumes.map((resume) => (
                  <div key={resume.uuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{resume.original_filename}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>{Math.round(resume.file_size / 1024)} KB</span>
                          <span>•</span>
                          <span>{new Date(resume.created_at).toLocaleDateString()}</span>
                          {resume.is_loaded_by_recruiter && (
                            <>
                              <span>•</span>
                              <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                Uploaded by Recruiter
                              </span>
                            </>
                          )}
                          {resume.is_primary && (
                            <>
                              <span>•</span>
                              <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                Primary
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Download Resume"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-4">No resumes uploaded yet</p>
            )}

            <div className="border-t border-gray-200 pt-4">
              <RecruiterResumeUpload
                candidateId={params.id}
                onUploadSuccess={handleResumeUploadSuccess}
                onError={handleResumeUploadError}
                disabled={uploadingResume}
              />
              
              {resumeUploadError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-xs text-red-700">{resumeUploadError}</p>
                </div>
              )}
            </div>
          </div>

          {/* AI Insights */}
          {candidate.aiInsights.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Insights</h3>
              <div className="space-y-2">
                {candidate.aiInsights.map((insight, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{insight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Associate Position Modal */}
      <AssociatePositionModal
        open={showAssociateModal}
        onClose={() => setShowAssociateModal(false)}
        candidate={candidate}
        onAssociated={() => {
          setShowAssociateModal(false)
          loadCandidateDetail() // Refresh candidate data to show new positions
        }}
      />
    </div>
  )
}
